import moment from "moment";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { type CostType } from "types/enum";

export type CostCodeDetailsFormActions =
    | undefined
    | "save"
    | "saveAndNew"
    | "saveAndClose"
    | "delete";

export interface ICostCodeDetailsFormValues {
    costCodeId: number;
    isActive: boolean;
    isLabor: boolean;
    isCustomerVariance: boolean;
    title: string;
    selectedCategoryId: number;
    parentId: number;
    currentType: CostType;
    details: string;
    defaultUnitCost: number;
}
export class ParentExtraData {
    constructor(data: any) {
        this.categoryId = data.categoryId;
        this.parentCostCodeId = data.parentCostCodeId;
    }
    categoryId: number;
    parentCostCodeId: number;
}
export class CostCodeEntity {
    constructor(data: any) {
        if (data.costCode) {
            // May not exist if cost code is deleted, however, in that case success will be false
            if (data.costCode.costCodeId) {
                this.costCodeId = data.costCode.costCodeId;
            } else {
                this.costCodeId = 0;
            }
            this.title = data.costCode.title;
            this.details = data.costCode.details;
            this.isActive = data.costCode.isActive;
            this.isCustomerVariance = data.costCode.isCustomerVariance;
            this.isVarianceCostCode = data.costCode.isVariance;
            this.inactivationDate = moment(data.costCode.inactivationDate);
            this.categoryList = data.categoryList;
            this.parentCostCodeId = data.costCode.parentCostCodeId;
            this.defaultUnitCost = data.costCode.defaultUnitCost;
            this.isLaborCode = data.costCode.isLaborCode;
            this.isFlatRateCode = data.costCode.isFlatRateCode;
            this.isHomeDepotCostCode = data.costCode.isHomeDepotCostCode;
            this.categoryId = data.costCode.categoryId;
            this.builderId = data.costCode.builderId;
        }

        this.isVariance = data.isVariance;
        this.shouldLockCostCode = data.shouldLockCostCode;
        this.byUserHourlyTracking = data.byUserHourlyTracking;
        this.canSave = data.canSave;
        this.canDelete = data.canDelete;
        this.categoryList = data.categoryList
            ? data.categoryList.map((category: any) => new BTSelectItem(category))
            : [];
        this.parentList = data.parentList
            ? data.parentList.map(
                  (parent: any) =>
                      new BTSelectItem<ParentExtraData>({
                          ...parent,
                          extraData: {
                              isDisabled: parent.isDisabled,
                              categoryId: parent.categoryId,
                              parentCostCodeId: parent.parentCostCodeId,
                          },
                      }),
              )
            : [];
        this.currentType = data.currentType; // Does not accurately reflect what type id points to, just depends on request.currentType
        this.friendlyName = data.friendlyName; // Does not accurately reflect what type id points to, just depends on request.currentType
    }

    costCodeId: number;
    title: string;
    details: string;
    isActive: boolean;
    isCustomerVariance: boolean;
    isVarianceCostCode: boolean; // Different from isVariance, this one represents the actual value in the db
    inactivationDate: moment.Moment;
    parentCostCodeId: number;
    defaultUnitCost: number;
    isLaborCode: boolean;
    isFlatRateCode: boolean;
    isHomeDepotCostCode: boolean;
    categoryId: number;
    builderId: number;
    isVariance: boolean; // Different from isVarianceCostCode, this one represents the value passed to endpoint in urlparam "currentType"
    shouldLockCostCode: boolean;
    byUserHourlyTracking: boolean;
    canSave: boolean;
    canDelete: boolean;
    categoryList: BTSelectItem[];
    parentList: BTSelectItem<ParentExtraData>[];
    currentType: CostType;
    friendlyName: string;
}

export class CostCodeUpdateResponse {
    constructor(data: any) {
        this.selectItem = new BTSelectItem(data.selectItem);
    }

    selectItem: BTSelectItem;
}

export interface ICostCodeDeleteRequest {
    costCodeId: number;
    currentType: CostType;
}

export class CostCodeDeleteResponse {
    constructor(data: any) {
        this.selectItem = data.selectItem ? new BTSelectItem(data.selectItem) : null;
        this.invalidCostCode = data.invalidCostCode;
        this.costCodeInUsage = data.costCodeInUsage;
        this.attachedItemCount = data.attachedItemCount;
    }

    selectItem: BTSelectItem | null;
    invalidCostCode: boolean;
    costCodeInUsage: boolean;
    attachedItemCount: number;
}

export class CostCodeConflictForm {
    constructor(data: any) {
        this.conflictList = data.conflictList
            ? data.conflictList.map((data: any) => new CostCodeConflict(data))
            : [];
    }
    conflictList: CostCodeConflict[];
}

export class CostCodeConflict {
    constructor(data: any) {
        this.title = data.title;
        this.type = data.type;
        this.jobsiteName = data.jobsiteName;
    }
    title: string;
    type: string;
    jobsiteName: string;
}

export type CostCategoryType = "Cost" | "Variance";
